import React from 'react';
import useUserInfo from '@/auth/useUserInfo';
import { type LaunchDarklyFlags } from '@/common/launchDarkly';
import { useIsSkySlopeMobileApp, checkIfRecipientIsSender } from '@/common/utils';
import { useShallowSelector } from '@/lib/reduxHooks';
import { postMessageToMobileApp } from '@/utils/post-message';

export function useMobileAppSignNow(args: { flags?: LaunchDarklyFlags }) {
  const userInfo = useUserInfo();
  const isMobileApp = useIsSkySlopeMobileApp();
  const signNowFlag = args.flags?.['sign-now'];

  const state = useShallowSelector((state) => ({
    done: state.senderBuild.done,
    status: state.senderBuild.status,
    signers: state.senderBuild.signers,
  }));

  React.useEffect(() => {
    if (!isMobileApp || !userInfo || !state.signers) return;

    if (state.done && state.status === 'Sent') {
      // Delegates excluded for the same reason as useSignNow: senderEmail is the
      // owner's while delegating, so the match below succeeds for them. useSignNow
      // does not install window.mobileApp.signNow for a delegate, so the native
      // button would do nothing.
      let signNowEnabled =
        signNowFlag &&
        !userInfo.isDelegate &&
        state.signers.some(
          (s) => checkIfRecipientIsSender(s) && userInfo.senderEmail === s.email && s.signingGroup === 'default'
        );

      if (signNowEnabled) {
        postMessageToMobileApp({ status: 'sent', message: 'signNowEnabled' });
      } else {
        postMessageToMobileApp({ status: 'sent' });
      }
    } else if (state.status === 'Error') {
      let message = 'There was an error sending the envelope.';
      postMessageToMobileApp({ status: 'error', message });
    }
  }, [isMobileApp, signNowFlag, state.done, state.signers, state.status, userInfo]);
}
